/**
 * XREF (External Reference) suite — Phase FIN.3.
 *
 * AutoCAD's XREF workflow lets a drawing reference an external DXF/DWG
 * file. The external content is loaded into a special BLOCK_RECORD and
 * inserted via standard INSERT. The user can:
 *
 *   - XATTACH        attach a new external file (browser File API → parser
 *                    → register block + INSERT)
 *   - XBIND          convert xref → local block (drops the external link;
 *                    the block flags lose the xref bits and the entities
 *                    remain inline). Subsequent saves carry the block as a
 *                    regular definition.
 *   - XUNLOAD        suppress the external content without deleting the
 *                    record. Renderer paints a grey placeholder. Status
 *                    flips to "unloaded".
 *   - XRELOAD        re-parse the external file (or update with new
 *                    content). Status flips back to "resolved".
 *   - XDETACH        remove the xref entirely (block, record, registry,
 *                    and every INSERT that targets it).
 *   - XCLIP          attach a 2D clipping boundary that the renderer
 *                    intersects against on draw.
 *
 * Browser limitation: direct file system access is not allowed. XATTACH
 * therefore takes pre-parsed content as input — the UI opens a file
 * picker, hands the file to the existing DXF parser, then dispatches
 * this mutation with the resulting entities.
 *
 * Every mutation is pure (input document never mutated) and returns a
 * fresh `DrawingDocument` that has been re-validated through
 * `assembleDrawingDocument(...)` (Zod parse in dev, invariants check in prod).
 */

import type {
  BlockChildEntity,
  BlockDefinition,
  BlockRecord,
  DrawingDocument,
  DrawingPoint,
  XrefAttachmentType,
  XrefClipBoundary,
  XrefRegistryEntry,
} from "@dxf-ai-editor/drawing-model";
import { assembleDrawingDocument } from "@dxf-ai-editor/drawing-model";

import { validateBlockName } from "./blockMutations";

// ═══════════════════════════════════════════════════════════════
// Flag bit constants (DXF group 70 on BLOCK header)
// ═══════════════════════════════════════════════════════════════

/** Bit 4 (value 4): block is an external reference (xref). */
export const XREF_FLAG_BIT = 0x04;
/** Bit 8 (value 8): block is an xref overlay (no nesting into parents). */
export const XREF_OVERLAY_FLAG_BIT = 0x08;

/** Combined mask used to recognise any xref flavour. */
export const XREF_FLAGS_MASK = XREF_FLAG_BIT | XREF_OVERLAY_FLAG_BIT;

/**
 * True when the block definition's `flags` mark it as an xref or xref
 * overlay. Centralised so callers do not duplicate the bit math.
 */
export function isXrefBlock(blockDef: Pick<BlockDefinition, "flags">): boolean {
  return (blockDef.flags & XREF_FLAGS_MASK) !== 0;
}

// ═══════════════════════════════════════════════════════════════
// XATTACH
// ═══════════════════════════════════════════════════════════════

export interface XattachInput {
  drawingDocument: DrawingDocument;
  /** Name of the new xref BLOCK_RECORD. Usually filename without .dxf. */
  xrefName: string;
  /** External path captured from the file picker (relative or absolute). */
  xrefPath: string;
  /**
   * Pre-parsed entities to bake into the xref BLOCK definition. Caller
   * is responsible for parsing the external file and re-parenting the
   * entities under the xref block's spaceId. Empty array is allowed —
   * AutoCAD shows a "not-found" placeholder until XRELOAD repopulates.
   */
  xrefEntities: BlockChildEntity[];
  attachmentType: XrefAttachmentType;
  /** Insertion point in the host space for the auto-generated INSERT. */
  insertionPoint: DrawingPoint;
  /** Active space id where the INSERT should land. */
  insertSpaceId: string;
  /** Allocator for the new INSERT entity id. */
  allocateInsertId?: () => string;
  /**
   * Optional handle to assign to the xref BLOCK_RECORD. When omitted the
   * DXF writer fills it on next export.
   */
  xrefBlockHandle?: string;
}

export interface XattachResult {
  updatedDocument: DrawingDocument;
  /** Id of the newly-created INSERT in the host space. */
  insertId: string;
  /** Generated block name (sanitised xrefName). */
  blockName: string;
  summary: string;
}

/**
 * Sanitise an xrefName to a valid block name: trim whitespace, replace
 * forbidden characters with `_`, and clamp to 255 chars. Returns the
 * sanitised name; throws when the name reduces to empty.
 */
function sanitiseXrefBlockName(rawName: string): string {
  const trimmed = rawName.trim();
  if (trimmed.length === 0) {
    throw new Error("XATTACH: xrefName must not be empty");
  }
  // Strip a trailing .dxf / .dwg if the user passed a filename.
  const stripped = trimmed.replace(/\.(dxf|dwg)$/i, "");
  // Replace forbidden chars (DXF symbol-name rules: |<>/\\:;,"?*=) with _.
  const sanitised = stripped.replace(/[|<>/\\:;,"?*=]/g, "_");
  if (sanitised.length === 0) {
    throw new Error("XATTACH: xrefName reduces to empty after sanitisation");
  }
  return sanitised.slice(0, 255);
}

export function xattachMutation(input: XattachInput): XattachResult {
  const blockName = sanitiseXrefBlockName(input.xrefName);

  // Reject if a block with this name already exists. AutoCAD allows a
  // re-attach of the same xref (it acts like XRELOAD) but for clarity
  // we route that through XRELOAD explicitly — XATTACH always creates
  // a fresh registry entry.
  const validateError = validateBlockName(
    blockName,
    input.drawingDocument.blocks,
  );
  if (validateError === "duplicate") {
    throw new Error(
      `XATTACH: block "${blockName}" already exists; use XRELOAD or pick a new name`,
    );
  }
  if (validateError !== null) {
    throw new Error(`XATTACH: invalid xref name "${blockName}": ${validateError}`);
  }

  // Re-parent every xref entity under a fresh spaceId so they live
  // inside the BLOCK definition (xref BLOCK_RECORDs use a synthesised
  // spaceId derived from the block name).
  const xrefSpaceId = `xref-${blockName.toLowerCase()}`;
  const reparentedEntities: BlockChildEntity[] = input.xrefEntities.map(
    (entity) => ({
      ...entity,
      spaceId: xrefSpaceId,
    }),
  );

  const flagBit =
    input.attachmentType === "overlay"
      ? XREF_OVERLAY_FLAG_BIT
      : XREF_FLAG_BIT;

  const newBlockDef: BlockDefinition = {
    name: blockName,
    handle: input.xrefBlockHandle ?? "",
    layer: "0",
    flags: flagBit,
    basePoint: { x: 0, y: 0, z: 0 },
    xrefPathName: input.xrefPath,
    description: "",
    entities: reparentedEntities,
    xrefStatus: "resolved",
  };

  const newBlockRecord: BlockRecord = {
    name: blockName,
    insertionUnits: 0,
    explodable: false, // xrefs are not explodable until bound
    scalable: "non-uniform",
    isAnnotative: false,
    annotativeDecompositions: {},
    description: "",
  };

  const insertId =
    input.allocateInsertId?.() ??
    `insert-xref-${Date.now()}-${Math.floor(Math.random() * 1e6).toString(36)}`;
  const insertEntity = {
    id: insertId,
    spaceId: input.insertSpaceId,
    layer: "0",
    // EPIC-COLOR-UNION S1 — `color` is the typed `EntityColor` union. This is an
    // UNTYPED literal spread into `s.entities` and passed to
    // `assembleDrawingDocument`, which does NOT re-parse in PROD — so a raw
    // string would survive as the entity colour at runtime (DEV-only coercion).
    // Emit the typed union directly (mirrors blockMutations / blockReducerHelpers).
    color: { kind: "byLayer" as const },
    linetype: "ByLayer",
    lineweight: "ByLayer",
    transparency: "0",
    type: "Block" as const,
    blockName,
    insertionPoint: { x: input.insertionPoint.x, y: input.insertionPoint.y },
    scaleX: 1,
    scaleY: 1,
    scaleZ: 1,
    rotation: 0,
    columnCount: 1,
    rowCount: 1,
    columnSpacing: 0,
    rowSpacing: 0,
    extrusion: { x: 0, y: 0, z: 1 },
    attributes: [],
    customProperties: {},
  };

  // Locate target space and append the new INSERT.
  const insertSpace = input.drawingDocument.spaces.find(
    (s) => s.id === input.insertSpaceId,
  );
  if (!insertSpace) {
    throw new Error(`XATTACH: insertSpaceId "${input.insertSpaceId}" not found`);
  }
  const nextSpaces = input.drawingDocument.spaces.map((s) =>
    s.id === input.insertSpaceId
      ? { ...s, entities: [...s.entities, insertEntity] }
      : s,
  );

  const registryEntry: XrefRegistryEntry = {
    name: blockName,
    path: input.xrefPath,
    status: "resolved",
    attachmentType: input.attachmentType,
    lastLoadedAt: Date.now(),
  };

  const updatedDocument = assembleDrawingDocument({
    ...input.drawingDocument,
    spaces: nextSpaces,
    blocks: {
      ...input.drawingDocument.blocks,
      [blockName]: newBlockDef,
    },
    blockRecords: {
      ...input.drawingDocument.blockRecords,
      [blockName]: newBlockRecord,
    },
    xrefRegistry: {
      ...input.drawingDocument.xrefRegistry,
      [blockName]: registryEntry,
    },
  });

  const summary = `XATTACH ${blockName} (${input.attachmentType}) from "${input.xrefPath}"; INSERT at ${input.insertionPoint.x},${input.insertionPoint.y}`;
  return { updatedDocument, insertId, blockName, summary };
}

// ═══════════════════════════════════════════════════════════════
// XBIND — convert xref → local block
// ═══════════════════════════════════════════════════════════════

export interface XbindInput {
  drawingDocument: DrawingDocument;
  xrefName: string;
}

export interface XbindResult {
  updatedDocument: DrawingDocument;
  summary: string;
}

export function xbindMutation(input: XbindInput): XbindResult {
  const blockDef = input.drawingDocument.blocks[input.xrefName];
  if (!blockDef) {
    throw new Error(`XBIND: xref "${input.xrefName}" not found`);
  }
  if (!isXrefBlock(blockDef)) {
    throw new Error(`XBIND: block "${input.xrefName}" is not an xref`);
  }

  // Strip xref + xref-overlay flags. The block becomes a regular local
  // definition. Cached content (xrefLoadedContent), xrefPathName, and
  // xrefStatus are reset so subsequent saves emit a clean BLOCK record.
  const cleanedFlags = blockDef.flags & ~XREF_FLAGS_MASK;
  const nextBlockDef: BlockDefinition = {
    ...blockDef,
    flags: cleanedFlags,
    xrefPathName: "",
    xrefStatus: "resolved",
    xrefLoadedContent: undefined,
  };

  // BlockRecord becomes explodable on bind (regular blocks are explodable
  // by default; xrefs are not).
  const blockRecord = input.drawingDocument.blockRecords[input.xrefName];
  const nextBlockRecord: BlockRecord = blockRecord
    ? { ...blockRecord, explodable: true }
    : {
        name: input.xrefName,
        insertionUnits: 0,
        explodable: true,
        scalable: "non-uniform",
        isAnnotative: false,
        annotativeDecompositions: {},
        description: "",
      };

  // Drop the xref registry entry — the block is now local.
  const nextRegistry: Record<string, XrefRegistryEntry> = {};
  for (const [k, v] of Object.entries(input.drawingDocument.xrefRegistry)) {
    if (k !== input.xrefName) nextRegistry[k] = v;
  }

  const updatedDocument = assembleDrawingDocument({
    ...input.drawingDocument,
    blocks: {
      ...input.drawingDocument.blocks,
      [input.xrefName]: nextBlockDef,
    },
    blockRecords: {
      ...input.drawingDocument.blockRecords,
      [input.xrefName]: nextBlockRecord,
    },
    xrefRegistry: nextRegistry,
  });

  const summary = `XBIND ${input.xrefName}: xref converted to local block`;
  return { updatedDocument, summary };
}

// ═══════════════════════════════════════════════════════════════
// XUNLOAD — suppress without removing
// ═══════════════════════════════════════════════════════════════

export interface XunloadInput {
  drawingDocument: DrawingDocument;
  xrefName: string;
}

export function xunloadMutation(input: XunloadInput): {
  updatedDocument: DrawingDocument;
  summary: string;
} {
  const blockDef = input.drawingDocument.blocks[input.xrefName];
  if (!blockDef) {
    throw new Error(`XUNLOAD: xref "${input.xrefName}" not found`);
  }
  if (!isXrefBlock(blockDef)) {
    throw new Error(`XUNLOAD: block "${input.xrefName}" is not an xref`);
  }

  const nextBlockDef: BlockDefinition = {
    ...blockDef,
    xrefStatus: "unloaded",
    // Keep xrefLoadedContent — reload restores it without re-parsing if
    // the cached content is still valid. The renderer hides entities for
    // unloaded xrefs regardless of cache.
  };
  const registryEntry = input.drawingDocument.xrefRegistry[input.xrefName];
  const nextRegistry = {
    ...input.drawingDocument.xrefRegistry,
    [input.xrefName]: registryEntry
      ? { ...registryEntry, status: "unloaded" as const }
      : {
          name: input.xrefName,
          path: blockDef.xrefPathName,
          status: "unloaded" as const,
          attachmentType: "attach" as const,
        },
  };

  const updatedDocument = assembleDrawingDocument({
    ...input.drawingDocument,
    blocks: { ...input.drawingDocument.blocks, [input.xrefName]: nextBlockDef },
    xrefRegistry: nextRegistry,
  });

  return {
    updatedDocument,
    summary: `XUNLOAD ${input.xrefName}: external content suppressed`,
  };
}

// ═══════════════════════════════════════════════════════════════
// XRELOAD — re-parse external content
// ═══════════════════════════════════════════════════════════════

export interface XreloadInput {
  drawingDocument: DrawingDocument;
  xrefName: string;
  /**
   * Optional fresh entity content from the (re-)parsed external file.
   * When omitted the existing cached entities stay; only the status
   * flips back to "resolved" — useful after XUNLOAD.
   */
  newEntities?: BlockChildEntity[];
  /** Optional new path (XRELOAD path supports re-pointing). */
  newPath?: string;
}

export function xreloadMutation(input: XreloadInput): {
  updatedDocument: DrawingDocument;
  summary: string;
} {
  const blockDef = input.drawingDocument.blocks[input.xrefName];
  if (!blockDef) {
    throw new Error(`XRELOAD: xref "${input.xrefName}" not found`);
  }
  if (!isXrefBlock(blockDef)) {
    throw new Error(`XRELOAD: block "${input.xrefName}" is not an xref`);
  }

  const xrefSpaceId = `xref-${input.xrefName.toLowerCase()}`;
  const nextEntities = input.newEntities
    ? input.newEntities.map((e) => ({ ...e, spaceId: xrefSpaceId }))
    : blockDef.entities;
  const nextPath = input.newPath ?? blockDef.xrefPathName;

  const nextBlockDef: BlockDefinition = {
    ...blockDef,
    entities: nextEntities,
    xrefPathName: nextPath,
    xrefStatus: "resolved",
  };

  const registryEntry = input.drawingDocument.xrefRegistry[input.xrefName];
  const nextRegistry = {
    ...input.drawingDocument.xrefRegistry,
    [input.xrefName]: registryEntry
      ? {
          ...registryEntry,
          path: nextPath,
          status: "resolved" as const,
          lastLoadedAt: Date.now(),
        }
      : {
          name: input.xrefName,
          path: nextPath,
          status: "resolved" as const,
          attachmentType: "attach" as const,
          lastLoadedAt: Date.now(),
        },
  };

  const updatedDocument = assembleDrawingDocument({
    ...input.drawingDocument,
    blocks: { ...input.drawingDocument.blocks, [input.xrefName]: nextBlockDef },
    xrefRegistry: nextRegistry,
  });

  return {
    updatedDocument,
    summary: input.newEntities
      ? `XRELOAD ${input.xrefName}: ${input.newEntities.length} entit${input.newEntities.length === 1 ? "y" : "ies"} loaded from "${nextPath}"`
      : `XRELOAD ${input.xrefName}: status restored to resolved`,
  };
}

// ═══════════════════════════════════════════════════════════════
// XDETACH — remove the xref entirely
// ═══════════════════════════════════════════════════════════════

export interface XdetachInput {
  drawingDocument: DrawingDocument;
  xrefName: string;
}

export function xdetachMutation(input: XdetachInput): {
  updatedDocument: DrawingDocument;
  summary: string;
  removedInsertCount: number;
} {
  const blockDef = input.drawingDocument.blocks[input.xrefName];
  if (!blockDef) {
    throw new Error(`XDETACH: xref "${input.xrefName}" not found`);
  }
  if (!isXrefBlock(blockDef)) {
    throw new Error(`XDETACH: block "${input.xrefName}" is not an xref`);
  }

  // Strip every INSERT that targets the xref from every space + every
  // other block definition (xrefs CAN nest inside other blocks via XBIND
  // before detach, so we walk both layers).
  let removedInsertCount = 0;
  const nextSpaces = input.drawingDocument.spaces.map((s) => ({
    ...s,
    entities: s.entities.filter((e) => {
      if (e.type === "Block" && e.blockName === input.xrefName) {
        removedInsertCount += 1;
        return false;
      }
      return true;
    }),
  }));

  const nextBlocks: Record<string, BlockDefinition> = {};
  for (const [name, def] of Object.entries(input.drawingDocument.blocks)) {
    if (name === input.xrefName) continue; // drop the xref block itself
    nextBlocks[name] = {
      ...def,
      entities: def.entities.filter((child) => {
        if (child.type === "Block" && child.blockName === input.xrefName) {
          removedInsertCount += 1;
          return false;
        }
        return true;
      }),
    };
  }

  const nextBlockRecords: Record<string, BlockRecord> = {};
  for (const [name, rec] of Object.entries(input.drawingDocument.blockRecords)) {
    if (name === input.xrefName) continue;
    nextBlockRecords[name] = rec;
  }

  const nextRegistry: Record<string, XrefRegistryEntry> = {};
  for (const [k, v] of Object.entries(input.drawingDocument.xrefRegistry)) {
    if (k !== input.xrefName) nextRegistry[k] = v;
  }

  const updatedDocument = assembleDrawingDocument({
    ...input.drawingDocument,
    spaces: nextSpaces,
    blocks: nextBlocks,
    blockRecords: nextBlockRecords,
    xrefRegistry: nextRegistry,
  });

  return {
    updatedDocument,
    removedInsertCount,
    summary: `XDETACH ${input.xrefName}: xref + ${removedInsertCount} INSERT reference(s) removed`,
  };
}

// ═══════════════════════════════════════════════════════════════
// XCLIP — set or remove a clipping boundary
// ═══════════════════════════════════════════════════════════════

export interface XclipInput {
  drawingDocument: DrawingDocument;
  xrefName: string;
  /**
   * Boundary to apply. Pass `null` to clear (XCLIP DELETE in AutoCAD).
   * Rectangle uses min/max corner pair (2 vertices); polygon uses an
   * ordered closed polygon (>=3 vertices).
   */
  boundary: XrefClipBoundary | null;
}

export function xclipMutation(input: XclipInput): {
  updatedDocument: DrawingDocument;
  summary: string;
} {
  const registryEntry = input.drawingDocument.xrefRegistry[input.xrefName];
  if (!registryEntry) {
    throw new Error(
      `XCLIP: xref "${input.xrefName}" not registered; XATTACH first`,
    );
  }

  if (input.boundary !== null) {
    if (input.boundary.type === "rectangle" && input.boundary.vertices.length !== 2) {
      throw new Error(
        "XCLIP: rectangle boundary requires exactly two vertices (min/max corners)",
      );
    }
    if (input.boundary.type === "polygon" && input.boundary.vertices.length < 3) {
      throw new Error(
        "XCLIP: polygon boundary requires at least three vertices",
      );
    }
  }

  const nextRegistry = {
    ...input.drawingDocument.xrefRegistry,
    [input.xrefName]:
      input.boundary === null
        ? (() => {
            const { clipBoundary: _drop, ...rest } = registryEntry;
            void _drop;
            return rest;
          })()
        : { ...registryEntry, clipBoundary: input.boundary },
  };

  const updatedDocument = assembleDrawingDocument({
    ...input.drawingDocument,
    xrefRegistry: nextRegistry,
  });

  return {
    updatedDocument,
    summary:
      input.boundary === null
        ? `XCLIP ${input.xrefName}: clip boundary removed`
        : `XCLIP ${input.xrefName}: ${input.boundary.type} boundary with ${input.boundary.vertices.length} vertex(ices) applied`,
  };
}
